/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory } from 'react-router-dom';
import { Divider, Button, Row, Col, Typography, Spin } from 'antd';
import styled from '@emotion/styled';
import { useRouteMatch } from 'react-router-dom';
import { message, Modal, Tooltip } from 'antd';
import HomeIcon from '~components/_common/HomeIcon/HomeIcon';
import navigationItems, {
  navigationItemsHome,
  navigationItemsTimeMachine,
} from '~src/data/navigationItems';
// COMMENTED OUT: Empty state now handled in FileTable
// import { EmptyProjectPannel } from '~components/atoms/EmptyprojectPannel/EmptyProjectPannel';
import { useIsProjectEmpty } from '~src/hooks/useIsProjectEmpty';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { Fragment, useContext, useCallback, useEffect, useMemo } from 'react';
import UploadDropZonePannel from '~components/atoms/UploadDropZonePannel/UploadDropZonePannel';
import UploadPanel from '~components/atoms/UploadPanel/UploadPanel';
import UploadContext from '~src/contexts/UploadContext';
import ShowUploadContext from '~src/contexts/ShowUploadDropZone';
import { useModules } from '~src/hooks/api-hooks/useModules';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { useIsPermissionManager } from '~src/hooks/useIsPermissionManager';
import { useProject } from '~src/hooks/api-hooks/useProject';
import ProjectIcon from '~src/assets/nav-icons/project.png';
import CompanyIcon from '~src/assets/c_web_logo_2x_color.png';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './style';
import { appLog } from '~src/utils/log';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { useProjectId } from '~src/hooks/useProjectId';
import {
  deletePrimarySVFRender,
  getPrimarySVFRender,
  getPrimarySVFRenderLocation,
} from '~src/services/api';
import { RightClickOverlay } from '~components/atoms/RightClickOverlay/RightClickOverlay';
import { RIGHTS_LEVEL } from '~src/types/enums';
import dataContextMenu from '~src/data/dataContextMenu';
import { useIntl } from 'react-intl';
import { useModalActions } from '~src/contexts/ModalsContext';
import { useIsUserAuthenticated } from '~src/hooks/api-hooks/useUserSettings';
import CachedSharesContext from '~src/contexts/CachedSharesContext';
import { useProjectList } from '~src/hooks/api-hooks/useProjectList';
import { useShares } from '~src/hooks/api-hooks/useShares';
import { BaseHomeNavigationKeys } from '~src/data/navigationItems';
import CachedPublicTenderContext from '~src/contexts/CachedPublicTender';

const { Text, Title } = Typography;

const StyledDivider = styled(Divider)`
  &.ant-divider-horizontal.ant-divider-with-text-left::before {
    top: 0;
  }
  &.ant-divider-horizontal.ant-divider-with-text-left::after {
    top: 0;
  }
`;

interface RightSideViewHomeProps {
  children?: any;
  showProjectInfo?: boolean;
}
const RightSideViewHome = ({
  children,
  showProjectInfo = false,
}: RightSideViewHomeProps) => {
  const style = useStyles(styleFn);

  const isProjectEmpty = useIsProjectEmpty();
  const isSuperuser = useIsSuperuser();
  const modules = useModules();
  const archiveSettings = useArchiveSettings();
  const isPermissionManager = useIsPermissionManager();
  const projectQuery = useProject();
  const intl = useIntl();
  const isTenderingOn =
    archiveSettings.data?.resArchiveSettings?.is_tendering_on;

  const isOnHome = window.location.pathname === '/';
  const isAuthenticated = useIsUserAuthenticated();

  const projectId = useProjectId();
  const { uploadContext } = useContext(UploadContext);
  const { showUploadZone, setShowUploadZone } = useContext(ShowUploadContext);
  const { setPrimaryPreviewFile, setFileToView } = useFileViewerContext();
  const primaryPreviewInfo = useModalActions('primaryPreviewInfo');

  const { cachedSharesContext } = useContext(CachedSharesContext);
  const { cachedPublicTendersContext } = useContext(CachedPublicTenderContext);

  const projectListQuery = useProjectList({ refetchOnMount: false });
  const sharesQuery = useShares({});

  appLog('archiveSettings', archiveSettings);

  const history = useHistory();

  // Check which home features are active
  const hasInvitations = cachedSharesContext && cachedSharesContext.length > 0;
  const hasPublicTenders =
    cachedPublicTendersContext && cachedPublicTendersContext.length > 0;
  const hasProjects = projectListQuery.data && projectListQuery.data.length > 0;
  const hasMyTenders =
    sharesQuery.data &&
    sharesQuery.data.some((share: any) => share?.module === 15);

  const clicked = (path: string) => {
    const urlPath = path.length > 1 ? path : '';

    if (isOnHome) {
      history.push(urlPath);
    } else {
      history.push(`/projects/${projectId + urlPath}`);
    }
  };

  const moduleItems = useMemo(
    () =>
      navigationItems.filter(
        (v) =>
          v.section === 'special' &&
          modules.data &&
          v.moduleName &&
          modules.data[v.moduleName]
      ),
    [modules]
  );

  // same logic as HomeMobile.openPrimarySVFfile but desktop version
  const openPrimarySVFfile = useCallback(async () => {
    if (!projectId) {
      appLog('[RightSideViewHome] Missing projectId, cannot open primary SVF');
      return;
    }

    try {
      const meta = await getPrimarySVFRender(Number(projectId)); // Now returns null on 404
      if (!meta) {
        message.error(
          intl.formatMessage({ id: 'primary-svf-file-preview-error-null' })
        );
        return;
      }

      const loc = await getPrimarySVFRenderLocation(Number(projectId));
      if (!meta?.file_guid || !loc?.urn || !loc?.access_token) {
        message.error(
          intl.formatMessage({ id: 'primary-svf-file-preview-error-null' })
        );
        return;
      }

      //close previous previews
      setFileToView(undefined);

      setPrimaryPreviewFile({
        id: meta.file_guid || 0,
        title: meta.versioned_file_title,
        file_name: meta.versioned_file_title,
        folder_id: meta.versioned_file_folder_id,
        serial_number: meta.file_serial_number,
        autodesk_object_id: meta.autodesk_object_id,
        urn: loc.urn,
        access_token: loc.access_token,
      } as any);
    } catch (err: any) {
      appLog('[RightSideViewHome] openPrimarySVFfile error', err);
      const status = err?.response?.status;
      if (status === 404) {
        message.error(
          intl.formatMessage({ id: 'primary-svf-file-preview-error-null' })
        );
      } else if (status === 403) {
        message.error(
          intl.formatMessage({
            id: 'primary-svf-file-preview-error-permission',
          })
        );
      } else {
        message.error(
          intl.formatMessage({
            id: 'primary-svf-file-preview-error',
          })
        );
      }
    }
  }, [projectId, setPrimaryPreviewFile]);

  // RightClick menu for preview
  const handlePrimaryDeleteMenuClick = async () => {
    if (!projectId) return;

    try {
      appLog(
        '[RightSideViewHome] deleting primary SVF render...',
        projectId,
        !archiveSettings?.data?.resArchiveSettings?.primary_svf_render_id
      );

      if (!archiveSettings?.data?.resArchiveSettings?.primary_svf_render_id) {
        message.error(
          intl.formatMessage({ id: 'primary-svf-file-preview-error-null' })
        );
        return;
      }

      Modal.confirm({
        centered: true,
        title: intl.formatMessage({
          id: 'delete-primary-preview',
        }),
        content: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            {intl.formatMessage({ id: 'delete-primary-text' })}
          </div>
        ),
        okText: intl.formatMessage({ id: 'ok' }),
        cancelText: intl.formatMessage({
          id: 'cancel',
        }),
        onOk: async () => {
          await deletePrimarySVFRender(Number(projectId));
          setPrimaryPreviewFile(undefined);
          archiveSettings.refetch?.();
        },
      });
    } catch (err) {
      appLog('[RightSideViewHome] deletePrimarySVFRender error', err);
    }
  };

  const handlePrimaryContextAction = (item: {
    modal?: string;
    name?: string;
  }) => {
    switch (item?.modal) {
      case 'delete-primary-preview':
        return handlePrimaryDeleteMenuClick();
      case 'details-primary-preview':
        primaryPreviewInfo.open({});
        return;
      default:
        return;
    }
  };

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.stopImmediatePropagation();
      if (!showUploadZone) {
        // show drop target, when dragging file into window
        setShowUploadZone(true);
      }
    };

    document.addEventListener('dragenter', handleDragEnter);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
    };
  }, [showUploadZone]);

  const { data } = useProject(true, true);

  const handleRedirect = () => {
    history.push('/login');
  };

  const isDataLoading = projectListQuery.isLoading || sharesQuery.isLoading;

  return (
    <div css={style.container}>
      {showProjectInfo && (
        <div css={style.containerProjectData}>
          <div css={style.leftColumn}>
            <StyledDivider orientation="left" plain css={style.divider}>
              {intl.formatMessage({ id: 'project-data' })}
            </StyledDivider>
            <div css={style.info}>
              <div css={style.projectInfo}>
                <img
                  src={data?.contact_data?.logo_url || ProjectIcon}
                  alt=""
                  width={80}
                  height={80}
                  style={{ objectFit: 'scale-down' }}
                />
                <div css={style.projectName}>
                  <h4>{projectQuery.data?.name}</h4>
                  <h5 style={{ color: 'DarkGrey' }}>
                    {data?.project_data?.case_nr}
                  </h5>
                  <Button
                    type="primary"
                    onClick={() => history.push(`/projects/${projectId}`)}>
                    {intl.formatMessage({ id: 'open-project' })}
                  </Button>
                </div>
              </div>
              <div css={style.customerInfo}></div>
              <div></div>
            </div>
            {/* Project Data*/}
            <div css={style.detailsGrid}>
              {data?.contact_data?.company_name && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-name' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>{data?.contact_data?.company_name}</p>
                </div>
              )}
              {data?.contact_data?.company_name_2 && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-name-second' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>
                    {data?.contact_data?.company_name_2}
                  </p>
                </div>
              )}
              {data?.contact_data?.address && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-address' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>{data?.contact_data?.address}</p>
                </div>
              )}
              {data?.contact_data?.zipcode && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-zipcode' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>{data?.contact_data?.zipcode}</p>
                </div>
              )}
              {data?.contact_data?.city && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-city' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>{data?.contact_data?.city}</p>
                </div>
              )}
              {data?.contact_data?.phone && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-phone' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>{data?.contact_data?.phone}</p>
                </div>
              )}
              {data?.contact_data?.fax && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-fax' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>{data?.contact_data?.fax}</p>
                </div>
              )}
              {data?.contact_data?.cvr && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-cvr' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>{data?.contact_data?.cvr}</p>
                </div>
              )}
              {data?.contact_data?.email && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-email' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>{data?.contact_data?.email}</p>
                </div>
              )}
              {data?.contact_data?.http && (
                <div css={style.detailItem}>
                  <p css={style.labelCss}>
                    <strong>
                      {intl.formatMessage({ id: 'contact-http' })}:&nbsp;
                    </strong>
                  </p>
                  <p css={style.valueCss}>{data?.contact_data?.http}</p>
                </div>
              )}
            </div>
          </div>
          {/* Company Contact Data*/}
          <div css={style.rightColumn}>
            {data?.customer_data === null ? (
              ''
            ) : (
              <div css={style.companyInfo}>
                <img
                  src={data?.customer_data?.logo || CompanyIcon}
                  alt="Company Logo"
                  css={style.companyLogo}
                />
                <div css={style.companyDetails}>
                  <p>
                    {data?.customer_data?.name ||
                      intl.formatMessage({ id: 'customer-name' })}
                    ,{' '}
                  </p>
                  <p>
                    {data?.customer_data?.address ||
                      intl.formatMessage({ id: 'customer-street' })}
                  </p>
                  <p>
                    {data?.customer_data?.zipcode ||
                      intl.formatMessage({ id: 'customer-zipcode' })}{' '}
                    {data?.customer_data?.city ||
                      intl.formatMessage({ id: 'customer-city' })}
                  </p>
                  <p>
                    {intl.formatMessage({ id: 'phone' })}{' '}
                    {data?.customer_data?.contact_phone ||
                      intl.formatMessage({ id: 'customer-phone' })}
                  </p>
                  <p>
                    CVR{' '}
                    {data?.customer_data?.cvr ||
                      intl.formatMessage({ id: 'customer-cvr' })}
                  </p>
                  <p>
                    {data?.customer_data?.contact_email ||
                      intl.formatMessage({ id: 'customer-email' })}
                  </p>
                  {data?.customer_data?.contact_person && (
                    <p>
                      {data?.customer_data?.contact_person ||
                        intl.formatMessage({ id: 'customer-contact' })}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div css={{ height: '100%' }}>
        <Divider orientation="left" plain css={style.divider}>
          {intl.formatMessage({ id: isOnHome ? 'shortcuts' : 'data' })}
        </Divider>
        <Row>
          {(() => {
            const items = [
              ...(window.sessionStorage.getItem('appState') === 'timeMachine'
                ? navigationItemsTimeMachine
                : isOnHome
                ? navigationItemsHome
                : navigationItems),
            ];

            const filteredItems = items.filter(
              (v) =>
                v.section === 'project-data' &&
                v.key !== 'shared-with-me' &&
                !(
                  v.key === 'tenders' &&
                  (!isPermissionManager || !isTenderingOn)
                ) &&
                !(
                  !isAuthenticated &&
                  (v.key === 'my-tenders' ||
                    v.key === 'projectweb' ||
                    v.key === 'my-projects')
                )
            );

            // Separate active and inactive items for home page
            const activeItems = isOnHome
              ? filteredItems.filter((v) => {
                  if (v.key === BaseHomeNavigationKeys.CACHED_SHARES)
                    return hasInvitations;
                  if (v.key === BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS)
                    return hasPublicTenders;
                  if (v.key === BaseHomeNavigationKeys.PROJECTWEB)
                    return hasProjects;
                  if (v.key === BaseHomeNavigationKeys.MY_TENDERS)
                    return hasMyTenders;
                  return true;
                })
              : filteredItems;

            const inactiveItems = isOnHome
              ? filteredItems.filter((v) => {
                  // Only show in "Other features" if authenticated
                  // (otherwise they were already filtered out above)
                  if (v.key === BaseHomeNavigationKeys.CACHED_SHARES)
                    return !hasInvitations;
                  if (v.key === BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS)
                    return !hasPublicTenders;
                  if (v.key === BaseHomeNavigationKeys.PROJECTWEB)
                    return isAuthenticated && !hasProjects;
                  if (v.key === BaseHomeNavigationKeys.MY_TENDERS)
                    return isAuthenticated && !hasMyTenders;
                  return false;
                })
              : [];

            // Debug logging
            // console.log('🔍 [DEBUG-OTHER-FEATURES] Items Filtering:', {
            //   isOnHome,
            //   items,
            //   filteredItems,
            //   activeItems: activeItems.map((v) => v.key),
            //   inactiveItems: inactiveItems.map((v) => v.key),
            //   hasInvitations,
            //   hasProjects,
            //   hasMyTenders,
            // });

            if (isDataLoading) {
              return (
                <div css={style.loadingContainer}>
                  <Spin
                    size="large"
                    tip={intl.formatMessage({ id: 'loading' })}
                  />
                </div>
              );
            }

            return (
              <Fragment>
                {/* Welcome block shortcut */}
                {isOnHome && !isAuthenticated && (
                  <Col span={24}>
                    <div css={style.welcomeContainer}>
                      <Title level={3}>
                        {intl.formatMessage({
                          id: 'welcome-title',
                        })}
                      </Title>

                      {activeItems.length === 0 && (
                        <Text css={style.welcomeText}>
                          {intl.formatMessage({
                            id: 'welcome-message',
                          })}
                        </Text>
                      )}

                      <Button
                        type="primary"
                        size="large"
                        onClick={handleRedirect}
                        style={{ marginTop: '20px', marginBottom: '20px' }}>
                        {intl.formatMessage({
                          id: 'go-to-login',
                        })}
                      </Button>
                    </div>
                  </Col>
                )}

                {activeItems.map((navItem) => (
                  <Col key={navItem.key}>
                    {navItem.key === 'archive' ? (
                      <div style={{ position: 'relative' }}>
                        <HomeIcon
                          key={navItem.key}
                          name={navItem.nameTerm}
                          iconStyle={navItem.icon[0]}
                          icon={navItem.icon[1]}
                          customStyle={[style.icons, style[navItem.color]]}
                          click={() => clicked(navItem.path)}
                        />
                        {window.sessionStorage.getItem('appState') !==
                          'timeMachine' &&
                          showUploadZone && (
                            <UploadDropZonePannel
                              size="medium"
                              disableFolderUpload={!isSuperuser}
                              disableFileUpload={true}
                              onDismiss={() => {
                                setShowUploadZone(false);
                              }}
                            />
                          )}
                      </div>
                    ) : navItem.key === 'tenders' ||
                      navItem.key === 'my-tenders' ? (
                      <HomeIcon
                        key={navItem.key}
                        name={navItem.nameTerm}
                        iconStyle={navItem.icon[0]}
                        customIcon={navItem.customIcon as string}
                        customStyle={{ width: '40%' }}
                        click={() => clicked(navItem.path)}
                      />
                    ) : navItem.key === 'primary-svf-viewer' ? (
                      (() => {
                        const isPreviewAvailable =
                          archiveSettings?.data?.resArchiveSettings
                            ?.primary_svf_render_id;
                        const content = (
                          <HomeIcon
                            name={navItem.nameTerm}
                            iconStyle={navItem.icon[0]}
                            icon={navItem.icon[1]}
                            customStyle={[
                              style.icons,
                              isPreviewAvailable ? style.grayBlue : style.red,
                            ]}
                            click={openPrimarySVFfile}
                          />
                        );

                        if (isSuperuser) {
                          const contextItems = isPreviewAvailable
                            ? dataContextMenu.primaryPreviewContextItems
                            : dataContextMenu.deletedPrimaryPreviewContextItems;

                          return (
                            <Tooltip
                              placement="bottom"
                              title={intl.formatMessage({
                                id: 'right-click-tooltip-help',
                              })}
                              style={{ cursor: 'pointer' }}>
                              <RightClickOverlay
                                key={navItem.key}
                                items={contextItems}
                                permissionLevel={RIGHTS_LEVEL.MANAGE}
                                onItemClick={handlePrimaryContextAction}
                                onOverlayClose={() => {
                                  //
                                }}
                                selected={{ moduleFile: {} as any }}>
                                {content}
                              </RightClickOverlay>
                            </Tooltip>
                          );
                        }

                        return content;
                      })()
                    ) : (
                      <HomeIcon
                        key={navItem.key}
                        name={navItem.nameTerm}
                        iconStyle={navItem.icon[0]}
                        icon={navItem.icon[1]}
                        customStyle={[style.icons, style[navItem.color]]}
                        click={() => clicked(navItem.path)}
                      />
                    )}
                  </Col>
                ))}
                {isOnHome && inactiveItems.length > 0 && (
                  <Fragment>
                    <div style={{ width: '100%' }}>
                      <Divider orientation="left" plain css={style.divider}>
                        {intl.formatMessage({ id: 'other-features' })}
                      </Divider>
                    </div>
                    {inactiveItems.map((navItem) => (
                      <Col key={navItem.key}>
                        {navItem.key === 'my-tenders' ? (
                          <HomeIcon
                            key={navItem.key}
                            name={navItem.nameTerm}
                            iconStyle={navItem.icon[0]}
                            customIcon={navItem.customIcon as string}
                            customStyle={{ width: '40%' }}
                            disabled={true}
                          />
                        ) : (
                          <HomeIcon
                            key={navItem.key}
                            name={navItem.nameTerm}
                            iconStyle={navItem.icon[0]}
                            icon={navItem.icon[1]}
                            customStyle={[style.icons, style[navItem.color]]}
                            disabled={true}
                          />
                        )}
                      </Col>
                    ))}
                  </Fragment>
                )}
              </Fragment>
            );
          })()}
        </Row>

        {window.sessionStorage.getItem('appState') !== 'timeMachine' &&
          !!moduleItems.length && (
            <Fragment>
              <Divider orientation="left" plain css={style.divider}>
                {intl.formatMessage({ id: 'modules' })}
              </Divider>
              <Row>
                {moduleItems.map((navItem) => (
                  <Col key={navItem.key}>
                    <HomeIcon
                      key={navItem.key}
                      name={navItem.nameTerm}
                      iconStyle={navItem.icon[0]}
                      icon={navItem.icon[1]}
                      customIcon={navItem.customIcon}
                      customStyle={[style.icons, style[navItem.color]]}
                      click={() => clicked(navItem.path)}
                    />
                  </Col>
                ))}
              </Row>
            </Fragment>
          )}
      </div>
      <div css={style.panelsContainer}>
        {uploadContext.length > 0 && <UploadPanel key="upload" />}
        {/* COMMENTED OUT: Empty state now handled in FileTable */}
        {/* {isSuperuser &&
          isProjectEmpty &&
          window.sessionStorage.getItem('appState') !== 'timeMachine' && (
            <EmptyProjectPannel />
          )} */}
      </div>
      {window.location.pathname !== '/' && (
        <Fragment>
          <StyledDivider orientation="left" plain css={style.divider}>
            {intl.formatMessage({ id: 'recent-files' })}
          </StyledDivider>
          {children}
        </Fragment>
      )}
    </div>
  );
};

export default RightSideViewHome;
